import { useState } from 'react';
import { useAuth } from '@butternetwork/shared-auth';
import { shortAddress } from '../utils/address';
import { chainLabel } from '../chains/registry';
import { useTheme } from '../theme/ThemeProvider';
import {
  ChevronDownIcon,
  CopyIcon,
  CheckIcon,
  LogOutIcon,
  SunIcon,
  MoonIcon,
  MonitorIcon,
} from '../icons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

interface UserMenuProps {
  /**
   * Compact mode (used in collapsed sidebar): render just the avatar dot,
   * dropdown opens to the right.
   */
  compact?: boolean;
  /**
   * Where to position the dropdown relative to the trigger.
   *   'top' — opens above (use when menu sits at page bottom, e.g. sidebar footer)
   *   'bottom' — opens below (default; use in headers)
   */
  placement?: 'top' | 'bottom';
}

export function UserMenu({ compact = false, placement = 'bottom' }: UserMenuProps) {
  const { user, logout } = useAuth();
  const { preference, setPreference } = useTheme();
  const [copied, setCopied] = useState(false);

  if (!user) return null;

  const handleCopy = async (e: Event) => {
    // Keep the menu open while showing "Copied" feedback.
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(user.address);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      // ignore — clipboard blocked in insecure contexts
    }
  };

  const cycleTheme = (e: Event) => {
    e.preventDefault();
    const next = preference === 'light' ? 'dark' : preference === 'dark' ? 'system' : 'light';
    setPreference(next);
  };

  const side = compact ? 'right' : placement === 'top' ? 'top' : 'bottom';
  const sideOffset = compact ? 8 : 4;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={
            compact
              ? 'inline-flex items-center justify-center w-10 h-10 rounded-md text-foreground hover:bg-muted transition-colors data-[state=open]:bg-muted'
              : 'w-full inline-flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm font-medium text-foreground hover:bg-muted transition-colors data-[state=open]:bg-muted'
          }
        >
          {compact ? (
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" aria-hidden />
          ) : (
            <span className="w-4 h-4 shrink-0 flex items-center justify-center" aria-hidden>
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
            </span>
          )}
          {!compact && (
            <>
              <div className="flex flex-col items-start min-w-0 flex-1 text-left">
                <span className="font-mono tabular-nums text-xs truncate max-w-full leading-tight">
                  {shortAddress(user.address)}
                </span>
                <span className="text-[10px] text-muted-foreground leading-tight">
                  {chainLabel(user.chainId)}
                </span>
              </div>
              <ChevronDownIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            </>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent side={side} align="start" sideOffset={sideOffset} className="w-64">
        <div className="p-3 border-b">
          <div className="text-xs text-muted-foreground">Connected wallet</div>
          <div className="mt-1 font-mono text-xs break-all">{user.address}</div>
          <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden />
            {chainLabel(user.chainId)}
          </div>
        </div>

        <DropdownMenuItem onSelect={handleCopy}>
          {copied ? <CheckIcon className="w-4 h-4 text-emerald-600" /> : <CopyIcon className="w-4 h-4" />}
          {copied ? 'Copied' : 'Copy address'}
        </DropdownMenuItem>

        <DropdownMenuItem onSelect={cycleTheme} className="justify-between">
          <span className="flex items-center gap-2">
            {preference === 'light' && <SunIcon className="w-4 h-4" />}
            {preference === 'dark' && <MoonIcon className="w-4 h-4" />}
            {preference === 'system' && <MonitorIcon className="w-4 h-4" />}
            Theme
          </span>
          <span className="text-xs text-muted-foreground capitalize">{preference}</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onSelect={() => logout()}
          variant="destructive"
        >
          <LogOutIcon className="w-4 h-4" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
